import { WA_FUSS, WA_KNOPF, waBeispiel, waText } from "@talentkontor/domain";
import { t } from "../../../lib/sprache";

/**
 * WhatsApp-Hinweise je Ereignis (0159): Namen der Ereignisse und die Texte der Vorlagen, wie sie bei Meta eingereicht werden
 * (Kategorie „Utility“, Platzhalter {{1}} …). Die Vorschau setzt erfundene Beispielwerte ein. Dieselben Texte stehen in
 * docs/WHATSAPP.md für die Einreichung.
 */

export interface WhatsappEvent { key: string; template: string; default: boolean; enabled: boolean; custom: boolean }
/**
 * Verbundene Nummer der Agentur (0167); templates: Name → Stand je Sprache bei Meta (APPROVED, PENDING, REJECTED …);
 * template_categories (0180): Name → Kategorie je Sprache (UTILITY, MARKETING, AUTHENTICATION), Meta stuft selbst um.
 */
export interface WhatsappAccount {
  display_phone: string | null; verified_name: string | null; status: "connected" | "error"; templates: Record<string, Record<string, string>>;
  template_categories?: Record<string, Record<string, string>>; templates_checked_at?: string | null;
  last_error: string | null; connected_at: string;
}
export interface WhatsappOverview {
  mode: "off" | "dry_run" | "live"; ready: boolean; included: boolean; creators_total: number; creators_reachable: number; dry_run_30d: number;
  events: WhatsappEvent[];
  /** 0167: Versand für diese Agentur möglich (global freigegeben oder Pilot), Pilot, verbundene Nummer, Zahlen */
  available: boolean; pilot: boolean; account: WhatsappAccount | null; creators_stopped: number; sent_30d: number;
}

export function normalizeWhatsapp(r: Partial<WhatsappOverview> | null | undefined): WhatsappOverview {
  return {
    mode: r?.mode ?? "off", ready: !!r?.ready, included: !!r?.included, creators_total: Number(r?.creators_total ?? 0),
    creators_reachable: Number(r?.creators_reachable ?? 0), dry_run_30d: Number(r?.dry_run_30d ?? 0), events: r?.events ?? [],
    available: !!r?.available, pilot: !!r?.pilot, account: r?.account ?? null, creators_stopped: Number(r?.creators_stopped ?? 0),
    sent_30d: Number(r?.sent_30d ?? 0),
  };
}

export function whatsappEventLabel(key: string): string {
  switch (key) {
    case "chat": return t("Neue Chatnachricht der Agentur", "New chat message from the agency");
    case "invoice_request": return t("Rechnung anfordern", "Invoice requested");
    case "invoice_revision": return t("Rückfrage zur Rechnung", "Question about an invoice");
    case "invoice_rejected": return t("Rechnung abgelehnt", "Invoice rejected");
    case "payment_check": return t("Zahlungseingang bestätigen", "Confirm payment receipt");
    case "payment_confirmed": return t("Auszahlung unterwegs", "Payout on its way");
    case "contract_to_sign": return t("Vertrag zur Unterschrift", "Contract to sign");
    case "calendar_event": return t("Neuer Termin", "New appointment");
    case "calendar_event_cancelled": return t("Termin abgesagt", "Appointment cancelled");
    case "content_deadline_soon": return t("Content-Deadline naht", "Content deadline approaching");
    case "deliverable_draft_due": return t("Entwurf fällig", "Draft due");
    case "deliverable_publish_due": return t("Veröffentlichung fällig", "Publishing due");
    case "content_changes": return t("Änderungswünsche des Teams", "Changes requested by the team");
    case "brand_changes": return t("Änderungswünsche der Marke", "Changes requested by the brand");
    case "deliverable_overdue_talent": return t("Beitrag überfällig", "Post overdue");
    default: return key;
  }
}

/** Vorschau mit erfundenen Werten (Mia, die eigene Agentur, Beispielmarke); Text wie bei Meta eingereicht (packages/domain/src/whatsapp.ts). */
export function whatsappVorschau(template: string, agentur: string, lang: "de" | "en"): string {
  // 14.10.2026: Eine neue Vorlage aus der Datenbank, die diese App-Fassung noch nicht kennt, ließ die ganze Seite abstürzen
  try { return waText(template, waBeispiel(template, agentur, lang), lang); } catch { return "–"; }
}

/** Fußzeile und Knopf jeder Nachricht (für die Vorschau) */
export const whatsappFuss = (lang: "de" | "en") => WA_FUSS[lang];
export const whatsappKnopf = (lang: "de" | "en") => WA_KNOPF[lang];

/**
 * Kategorie einer Vorlage bei Meta über beide Sprachen (0180): Marketing, sobald Meta eine Sprache so einstuft (teurer, für
 * Creator mit Marketing-Grenzen bei WhatsApp); Service (UTILITY) wie eingereicht; null: noch unbekannt.
 */
export function vorlagenKategorie(categories: Record<string, Record<string, string>> | undefined, name: string): "marketing" | "utility" | "authentication" | "other" | null {
  const xs = Object.values(categories?.[name] ?? {}).map((s) => String(s).toUpperCase());
  if (!xs.length) return null;
  if (xs.includes("MARKETING")) return "marketing";
  if (xs.every((s) => s === "UTILITY")) return "utility";
  if (xs.every((s) => s === "AUTHENTICATION")) return "authentication";
  return "other";
}

/** Stand je Sprache als Text für den Tooltip („DE: APPROVED · EN: PENDING“) */
export function vorlagenDetail(account: Pick<WhatsappAccount, "templates" | "template_categories"> | null | undefined, name: string): string {
  const st = account?.templates?.[name] ?? {};
  const cat = account?.template_categories?.[name] ?? {};
  return ["de", "en"].map((l) => `${l.toUpperCase()}: ${st[l] ?? "–"}${cat[l] ? ` (${cat[l]})` : ""}`).join(" · ");
}

/** Stand einer Vorlage bei Meta über beide Sprachen: geprüft, in Prüfung, abgelehnt, fehlt */
export function vorlagenStand(templates: Record<string, Record<string, string>> | undefined, name: string): "approved" | "pending" | "rejected" | "missing" {
  const v = templates?.[name];
  if (!v || !Object.keys(v).length) return "missing";
  const xs = Object.values(v).map((s) => s.toUpperCase());
  if (xs.some((s) => s === "REJECTED" || s === "DISABLED" || s === "PAUSED")) return "rejected";
  if (xs.every((s) => s === "APPROVED") && xs.length >= 2) return "approved";
  return "pending";
}
